import { useCallback } from 'react';
import { useQuery } from 'src/shared/lib/useQuery';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { Flow } from 'src/features/flows/types/flow.types';
import { QueryResult } from 'src/shared/types/util.types';

export const BASE_PATH = '/api/flow/v2';

export function useFlow(): QueryResult<Flow[]>;
export function useFlow(flowId: string): QueryResult<Flow>;
export function useFlow(flowId?: string): QueryResult<Flow[] | Flow> {
  const { orgFetch, currentOrg } = useOrg();

  const queryKey = genQueryKey(currentOrg.id);

  // ✅ Memoize selector - stable reference unless flowId changes
  // TanStack Query uses structural sharing to prevent re-renders when flow data
  // reference changes but content is the same
  const selectFlow = useCallback(
    (flows: Flow[]) => {
      if (flowId !== undefined) {
        return flows?.find((flow) => flow.id === flowId);
      }
      return flows;
    },
    [flowId]
  );

  return useQuery({
    queryKey: ['flows', queryKey],
    queryFn: () => getData<Flow[]>('', 'failed to fetch flow'),
    staleTime: 0,
    select: selectFlow,
  });

  async function getData<T>(endPoint: string, errorMsg: string): Promise<T> {
    const res = await orgFetch<T>(`${BASE_PATH}/${endPoint}`);
    if (!res.ok) {
      throw new Error(errorMsg);
    }
    return await res.json();
  }
}
